import React, { useState, useEffect, useRef } from 'react';
import { cleanHtml } from '../utils/sanitize';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { getContact, deleteContact, deleteDocument, addHistorique, updateHistorique, deleteHistorique, setAuthToken, getUploadsUrl, getGatewayUploadsUrl, getTarifsForContact, getTarifsStandard, fetchMedia } from '../services/api';
import { toast } from 'react-toastify';
import Loading from '../components/Loading';
import ContactTarifsModal from '../components/ContactTarifsModal';
import ItineraireModal from '../components/ItineraireModal';
import { borneUrl } from '../lib/borneUrl';
import TarifReadOnlyGrid from '../components/TarifReadOnlyGrid';
import ContactMissionsTab from '../components/ContactMissionsTab';
import CopyButton from '../components/CopyButton';
import { Tag as TagIcon, Star as StarIcon } from 'lucide-react';
import { Gate } from '../lib/perms';
import './ContactDetail.css';

// App d'expédition (UPS). Configurable : l'adresse par défaut est une IP, elle
// ne survivra pas à un changement d'infra.
const UPS_URL = (process.env.REACT_APP_UPS_URL || 'http://ups.51.15.228.222.sslip.io').replace(/\/+$/, '');


const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).replace(',', ' à');
};

// Qualification labels
const getQualificationLabel = (qualification) => {
  const labels = {
    'antenne_principale': 'Antenne actuelle',
    'antenne_binome': 'Binôme',
    'historique_antenne': 'Ancienne antenne',
    'profil_intéressant': 'Profil intéressant'
  };
  return labels[qualification] || qualification;
};

// Qualification CSS class
const getQualificationClass = (qualification) => {
  const classes = {
    'antenne_principale': 'qualification-principale',
    'antenne_binome': 'qualification-binome',
    'historique_antenne': 'qualification-historique',
    'profil_intéressant': 'qualification-interessant'
  };
  return classes[qualification] || 'qualification-default';
};

// Calculate age from birth date
const calculateAge = (dateNaiss) => {
  if (!dateNaiss) return null;
  const birthDate = new Date(dateNaiss);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

// Calculate team duration
const calculateTeamDuration = (travailleDepuis) => {
  if (!travailleDepuis) return null;
  const startDate = new Date(travailleDepuis);
  const now = new Date();
  const diff = now - startDate;
  const years = Math.floor(diff / (365.25 * 24 * 60 * 60 * 1000));
  const months = Math.floor((diff % (365.25 * 24 * 60 * 60 * 1000)) / (30.44 * 24 * 60 * 60 * 1000));
  const days = Math.floor((diff % (30.44 * 24 * 60 * 60 * 1000)) / (24 * 60 * 60 * 1000));

  if (years > 0) {
    return `${years} ${years === 1 ? 'an' : 'ans'}`;
  } else if (months > 0) {
    return `${months} mois`;
  } else {
    return `${days} ${days === 1 ? 'jour' : 'jours'}`;
  }
};

// Rentrer selfizee labels
const getRentrerSelfizeeLabel = (value) => {
  const labels = {
    'prospection': 'prise de contact par un membre de l\'équipe',
    'contact': 'contact entrant',
    'connaissance': 'connaissance',
    'autre': 'une raison non précisée'
  };
  return labels[value] || 'une raison non précisée';
};

// L'URL des fiches bornes est paramétrable (réglage `integrations.bornes_url`
// ou REACT_APP_BORNES_URL_TEMPLATE) : cf. lib/borneUrl.js.
const getBorneDetailUrl = (borne) => borneUrl(borne);

// Detect borne type from number
const getBorneTypes = (bornes) => {
  const types = { classik: false, spherik: false, prestige: false };
  if (!bornes || !Array.isArray(bornes)) return types;

  bornes.forEach(borne => {
    const numero = (borne.numero_formated || borne.numero || '').toLowerCase();
    if (numero.startsWith('c')) types.classik = true;
    if (numero.startsWith('s')) types.spherik = true;
    if (numero.startsWith('p')) types.prestige = true;
  });
  return types;
};

// Check if file is an image based on extension
const isImageFile = (filename) => {
  if (!filename) return false;
  const ext = filename.split('.').pop().toLowerCase();
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext);
};

// Get file extension
const getFileExtension = (filename) => {
  if (!filename) return '';
  return filename.split('.').pop().toLowerCase();
};

// Check if file is a video
const isVideoFile = (filename) => {
  if (!filename) return false;
  const ext = filename.split('.').pop().toLowerCase();
  return ['mp4', 'mov', 'avi', 'webm'].includes(ext);
};

// Get icon and color for file type
const getFileTypeInfo = (filename) => {
  const ext = getFileExtension(filename);
  const types = {
    pdf: { color: '#dc2626', bg: '#fef2f2', label: 'PDF' },
    doc: { color: '#2563eb', bg: '#dbeafe', label: 'DOC' },
    docx: { color: '#2563eb', bg: '#dbeafe', label: 'DOCX' },
    xls: { color: '#16a34a', bg: '#dcfce7', label: 'XLS' },
    xlsx: { color: '#16a34a', bg: '#dcfce7', label: 'XLSX' },
    txt: { color: '#6b7280', bg: '#f3f4f6', label: 'TXT' },
    zip: { color: '#f59e0b', bg: '#fef3c7', label: 'ZIP' },
    rar: { color: '#f59e0b', bg: '#fef3c7', label: 'RAR' },
  };
  return types[ext] || { color: '#6b7280', bg: '#f3f4f6', label: ext.toUpperCase() || 'FILE' };
};

// Check if a value is effectively empty (null, undefined, '', '0', '<p>0</p>', etc.)
const isEmptyValue = (val) => {
  if (val === null || val === undefined || val === '') return true;
  const stripped = String(val).replace(/<[^>]*>/g, '').trim();
  return stripped === '' || stripped === '0';
};

// Check if contact was created within the last 2 months
const isNewContact = (contact) => {
  const createdDate = contact.created_at || contact.created;
  if (!createdDate) return false;
  const twoMonthsAgo = new Date();
  twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
  return new Date(createdDate) >= twoMonthsAgo;
};

function ContactDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('informations');
  // Tarif applique au contact (standard herite ou personnalise via overrides JSONB)
  const [tarifData, setTarifData] = useState(null);
  const [tarifStandard, setTarifStandard] = useState(null);
  const [tarifIsCustom, setTarifIsCustom] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showTarifsModal, setShowTarifsModal] = useState(false);
  const [showItineraireModal, setShowItineraireModal] = useState(false);
  const [activitiesExpanded, setActivitiesExpanded] = useState(false);
  const [activitiesFilter, setActivitiesFilter] = useState('all'); // 'all', 'comments', 'actions'
  const [previewFile, setPreviewFile] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [commentFiles, setCommentFiles] = useState([]);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [brokenFiles, setBrokenFiles] = useState(new Set());
  const [editingComment, setEditingComment] = useState(null);
  const [editCommentContent, setEditCommentContent] = useState('');
  const [editExistingFiles, setEditExistingFiles] = useState([]);
  const [editDeleteFileIds, setEditDeleteFileIds] = useState([]);
  const [editNewFiles, setEditNewFiles] = useState([]);
  const [savingComment, setSavingComment] = useState(false);
  const [deletingComment, setDeletingComment] = useState(null);
  const [commentHasContent, setCommentHasContent] = useState(false);
  const commentRef = useRef(null);
  const fileInputRef = useRef(null);
  const editCommentRef = useRef(null);
  const editFileInputRef = useRef(null);

  useEffect(() => {
    if (auth.user?.access_token) {
      setAuthToken(auth.user.access_token);
      loadContact();
    }
  }, [auth.user, id]);

  // Charge le tarif applique a ce contact (standard herite OU personnalise)
  // ainsi que le standard global pour identifier les overrides en surbrillance.
  useEffect(() => {
    if (!auth.user?.access_token || !id) return;
    Promise.all([
      getTarifsForContact(id),
      getTarifsStandard().catch(() => null),
    ])
      .then(([r, std]) => {
        setTarifData(r.data || null);
        setTarifIsCustom(!!r.isCustom);
        setTarifStandard(std || null);
      })
      .catch(() => { /* tarif optionnel - silent fail */ });
  }, [auth.user, id]);

  // Check file existence for historique files
  useEffect(() => {
    if (!contact?.historiques) return;
    const allFiles = contact.historiques.flatMap(h => (h.files || []).map(f => {
      const url = f.fichier?.includes('/') ? getUploadsUrl(f.fichier) : getUploadsUrl(`commentaires_clients/${f.fichier}`);
      return { id: f.id, url };
    }));
    if (allFiles.length === 0) return;

    const broken = new Set();
    Promise.all(allFiles.map(f =>
      // fetchMedia (et pas fetch) : sans le cookie de session média, l'API
      // répond 401 et TOUS les fichiers seraient signalés « manquants ».
      fetchMedia(f.url, { method: 'HEAD' }).then(res => {
        if (!res.ok) broken.add(f.id);
      }).catch(() => broken.add(f.id))
    )).then(() => setBrokenFiles(broken));
  }, [contact]);

  const loadContact = async () => {
    try {
      setLoading(true);
      const data = await getContact(id);
      setContact(data);
    } catch (error) {
      console.error('Error loading contact:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      setDeleting(true);
      await deleteContact(id);
      toast.success('Contact archivé');
      setShowDeleteModal(false);
      navigate('/contacts');
    } catch (error) {
      toast.error('Erreur lors de l\'archivage');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) return;

    try {
      await deleteDocument(docId);
      toast.success('Document supprimé');
      loadContact(); // Refresh the contact data
    } catch (error) {
      toast.error('Erreur lors de la suppression du document');
    }
  };

  // NOTE : la gestion des binômes (ajout / modification / suppression de
  // liaison) se fait depuis la fiche d'édition (ContactForm, onglet Binôme(s)).
  // La fiche de consultation est en lecture seule.

  // Comment form handlers
  const handleCommentFormat = (command) => {
    document.execCommand(command, false, null);
    commentRef.current?.focus();
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    const html = commentRef.current?.innerHTML || '';
    if (!html.replace(/<[^>]*>/g, '').trim() && commentFiles.length === 0) return;

    try {
      setSubmittingComment(true);
      await addHistorique(id, { type: 'commentaire', contenu: html }, commentFiles);
      if (commentRef.current) commentRef.current.innerHTML = '';
      setCommentFiles([]);
      setCommentHasContent(false);
      toast.success('Commentaire ajouté');
      loadContact();
    } catch (error) {
      toast.error("Erreur lors de l'ajout du commentaire");
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleCommentFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setCommentFiles(prev => [...prev, ...newFiles]);
    e.target.value = '';
  };

  const removeCommentFile = (index) => {
    setCommentFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Téléchargement forcé via blob (sinon le navigateur affiche le PDF au lieu
  // de le télécharger). Passe par fetchMedia : les uploads sont protégés par le
  // cookie de session média, que `fetch()` seul n'envoie pas en cross-origin.
  const forceDownload = async (url, filename) => {
    try {
      const resp = await fetchMedia(url);
      if (!resp.ok) throw new Error('Erreur de téléchargement');
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename || 'document';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60 * 1000);
    } catch (e) {
      toast.error(e.message || 'Erreur lors du téléchargement');
    }
  };

  // Drag & drop for comment files
  const [commentDragActive, setCommentDragActive] = useState(false);
  const handleCommentDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setCommentDragActive(true); };
  const handleCommentDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setCommentDragActive(false); };
  const handleCommentDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCommentDragActive(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length > 0) setCommentFiles(prev => [...prev, ...files]);
  };

  const handleEditComment = (h) => {
    setEditingComment(h);
    setEditCommentContent(h.contenu || h.commentaire || '');
    setEditExistingFiles(h.files || []);
    setEditDeleteFileIds([]);
    setEditNewFiles([]);
  };

  const handleSaveEditComment = async () => {
    if (!editingComment) return;
    const html = editCommentRef.current?.innerHTML || '';
    if (!html.replace(/<[^>]*>/g, '').trim() && editExistingFiles.length === 0 && editNewFiles.length === 0) return;

    try {
      setSavingComment(true);
      await updateHistorique(id, editingComment.id, { contenu: html }, editNewFiles, editDeleteFileIds);
      toast.success('Commentaire modifié');
      setEditingComment(null);
      loadContact();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Vous ne pouvez modifier que vos propres commentaires');
      } else {
        toast.error('Erreur lors de la modification');
      }
    } finally {
      setSavingComment(false);
    }
  };

  const handleEditRemoveExistingFile = (fileId) => {
    setEditExistingFiles(prev => prev.filter(f => f.id !== fileId));
    setEditDeleteFileIds(prev => [...prev, fileId]);
  };

  const handleEditAddFiles = (e) => {
    const newFiles = Array.from(e.target.files);
    setEditNewFiles(prev => [...prev, ...newFiles]);
    e.target.value = '';
  };

  const handleEditRemoveNewFile = (index) => {
    setEditNewFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDeleteComment = async () => {
    if (!deletingComment) return;

    try {
      await deleteHistorique(id, deletingComment.id);
      toast.success('Commentaire supprimé');
      setDeletingComment(null);
      loadContact();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Vous ne pouvez supprimer que vos propres commentaires');
      } else {
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const openPreview = async (file) => {
    setPreviewLoading(true);
    const ext = (file.fichier || '').split('.').pop().toLowerCase();
    const needsBlob = ['pdf', 'txt', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext);
    if (needsBlob && file.url && !file.url.startsWith('blob:')) {
      // For PDF/office files, fetch as blob first to bypass CSP frame-ancestors
      // restriction on the API origin. Blob URLs are same-origin so iframe works.
      // Set previewFile WITHOUT URL initially → iframe won't render with original URL.
      setPreviewFile({ ...file, url: '', originalUrl: file.url });
      try {
        const resp = await fetchMedia(file.url);
        if (!resp.ok) throw new Error('Fetch failed');
        const blob = await resp.blob();
        const blobUrl = URL.createObjectURL(blob);
        setPreviewFile({ ...file, url: blobUrl, originalUrl: file.url });
      } catch {
        // On failure, use original URL (might still fail with CSP but show error)
        setPreviewFile(file);
      }
    } else {
      setPreviewFile(file);
    }
  };

  // Cleanup blob URLs when preview closes
  useEffect(() => {
    return () => {
      if (previewFile?.url?.startsWith('blob:')) {
        URL.revokeObjectURL(previewFile.url);
      }
    };
  }, [previewFile?.url]);

  // Close preview modal on Escape key
  useEffect(() => {
    if (!previewFile) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setPreviewFile(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [previewFile]);

  // File URL helper for historique files (backward compat)
  const getHistoriqueFileUrl = (fichier) => {
    if (!fichier) return null;
    if (fichier.includes('/')) return getUploadsUrl(fichier);
    return getUploadsUrl(`commentaires_clients/${fichier}`);
  };

  if (loading) return <Loading />;

  if (!contact) {
    return (
      <div className="not-found">
        <h2>Contact non trouvé</h2>
        <Link to="/contacts" className="btn btn-primary">Retour</Link>
      </div>
    );
  }

  const hasLocation = contact.addr_lat && contact.addr_lng;
  const position = hasLocation ? [parseFloat(contact.addr_lat), parseFloat(contact.addr_lng)] : null;

  const tabs = [
    { id: 'informations', label: 'Informations', icon: '📋' },
    { id: 'documents', label: `Documents`, count: contact.documents?.length || 0, icon: '📄' },
    { id: 'materiel', label: `Matériel`, count: (contact.bornes?.length || 0) + (contact.accessoires?.length || 0), icon: '🖥️' },
    { id: 'tarif', label: 'Tarif', icon: '🏷️', highlight: tarifIsCustom },
    { id: 'binomes', label: `Binômes`, count: contact.binomes?.length || 0, icon: '👥' },
    // Onglet Missions masqué tant que l'app Antenne Missions n'est pas en prod.
    // { id: 'missions', label: 'Missions', icon: '🚚' },
  ];

  // Filter activities based on selected filter.
  // Une entrée est considérée comme "action" si is_story est truthy OU
  // si le type contient 'Action' (fallback défensif si is_story arrivait
  // mal typé depuis le backend).
  const isActionEntry = (h) => {
    if (h.is_story) return true;
    const t = (h.type || '').toString().toLowerCase();
    return t.includes('action') || t.includes('story') || t.includes('modification');
  };
  const filteredActivities = contact.historique?.filter(h => {
    if (activitiesFilter === 'comments') return !isActionEntry(h);
    if (activitiesFilter === 'actions') return isActionEntry(h);
    return true;
  }) || [];

  const currentUserId = auth.user?.profile?.user_id || null;
  const displayedActivities = activitiesExpanded ? filteredActivities : filteredActivities.slice(0, 5);

  return (
    <div id="contact" className="contact-detail">
      <header className="page-header">
        <div className="header-info">
          <Link to="/contacts" className="back-link">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6" />
            </svg>
            Retour
          </Link>
          <h1>{contact.antenne_ville || 'Contact'}</h1>
        </div>
        <div className="header-actions">
          <Gate perm="contacts.edit">
            <Link to={`/contacts/${id}/edit`} className="btn btn-secondary">Éditer</Link>
          </Gate>
          {/* Bouton "Supprimer" deplace vers la fiche edit (a cote du bouton Enregistrer)
              pour eviter une suppression accidentelle depuis la vue. */}
        </div>
      </header>

      <div className="detail-layout">
        {/* Sidebar */}
        <aside className="contact-sidebar">
          {/* Qualification Header */}
          {contact.qualification && (
            <div className={`qualification-header ${getQualificationClass(contact.qualification)}`}>
              {getQualificationLabel(contact.qualification)}
            </div>
          )}

          {/* New Contact Badge */}
          {isNewContact(contact) && (
            <span className="new-contact-tag">Nouveau</span>
          )}

          <div className="contact-sidebar-content">
            <div className="contact-avatar-lg">
            {contact.photo ? (
              <img src={getUploadsUrl(contact.photo)} alt={`${contact.prenom} ${contact.nom}`} />
            ) : contact.civilite === 'm' ? (
              <img src="/img/avatar-homme.svg" alt="Avatar homme" />
            ) : (
              <img src="/img/avatar-femme.svg" alt="Avatar femme" />
            )}
          </div>

          <h2 className="contact-name" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
            {contact.prioritaire && (
              <span title="Contact prioritaire" aria-label="Contact prioritaire"
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 26, height: 26, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                  boxShadow: '0 0 0 3px rgba(251,191,36,0.25)', fontSize: 14,
                  animation: 'pulse-prio 2s ease-in-out infinite',
                }}>
                ⭐
              </span>
            )}
            <span>{contact.prenom} {contact.nom}</span>
            {contact.prioritaire && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '2px 10px', borderRadius: 999,
                background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
                color: '#92400e', fontSize: 11, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: 0.5,
                border: '1px solid #f59e0b',
              }}>
                Prioritaire
              </span>
            )}
          </h2>

          {/* Ne plus contacter warning */}
          {contact.ne_plus_contacter && (
            <div className="warning-badge">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              Ne plus contacter
            </div>
          )}

          <p className="contact-role">
            {contact.situation_professionnelle}
            {contact.raison_sociale && ` - ${contact.raison_sociale}`}
            {contact.linkedin && (
              <a href={contact.linkedin} target="_blank" rel="noopener noreferrer" className="social-icon linkedin-icon" title="Profil LinkedIn">
                <svg viewBox="4 4 22 22">
                  <path d="M24,4H6C4.895,4,4,4.895,4,6v18c0,1.105,0.895,2,2,2h18c1.105,0,2-0.895,2-2V6C26,4.895,25.105,4,24,4z M10.954,22h-2.95v-9.492h2.95V22z M9.449,11.151c-0.951,0-1.72-0.771-1.72-1.72c0-0.949,0.77-1.719,1.72-1.719c0.948,0,1.719,0.771,1.719,1.719C11.168,10.38,10.397,11.151,9.449,11.151z M22.004,22h-2.948v-4.616c0-1.101-0.02-2.517-1.533-2.517c-1.535,0-1.771,1.199-1.771,2.437V22h-2.948v-9.492h2.83v1.297h0.04c0.394-0.746,1.356-1.533,2.791-1.533c2.987,0,3.539,1.966,3.539,4.522V22z" fill="currentColor"/>
                </svg>
              </a>
            )}
            {contact.site_internet && (
              <a href={contact.site_internet} target="_blank" rel="noopener noreferrer" className="social-icon" title="Site internet">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="2" y1="12" x2="22" y2="12" />
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                </svg>
              </a>
            )}
          </p>

          {/* Age */}
          {contact.date_naiss && calculateAge(contact.date_naiss) && (
            <p className="contact-age">
              {calculateAge(contact.date_naiss)} ans ({new Date(contact.date_naiss).toLocaleDateString('fr-FR')})
            </p>
          )}

          {/* Borne type badges */}
          {(() => {
            const borneTypes = getBorneTypes(contact.bornes);
            return (borneTypes.classik || borneTypes.spherik || borneTypes.prestige) && (
              <div className="contact-tags borne-types">
                {borneTypes.classik && <span className="tag tag-classik">Antenne Classik</span>}
                {borneTypes.spherik && <span className="tag tag-spherik">Antenne Spherik</span>}
                {borneTypes.prestige && <span className="tag tag-prestige">Antenne Prestige</span>}
              </div>
            );
          })()}

          {/* Type profils badges */}
          <div className="contact-tags">
            {contact.type_profils?.map(tp => (
              <span key={tp.id} className="tag tag-profil">{tp.nom}</span>
            ))}
          </div>

          {/* Bornes list */}
          {contact.bornes && contact.bornes.length > 0 && (
            <p className="bornes-list">
              Borne{contact.bornes.length > 1 ? 's' : ''} : {contact.bornes.map((b, i) => (
                <span key={b.id}>
                  {i > 0 && ', '}
                  <a href={getBorneDetailUrl(b) || undefined} target="_blank" rel="noopener noreferrer">{b.numero_formated || b.numero}</a>
                </span>
              ))}
            </p>
          )}

          {/* Notations — affichees sous la liste des bornes en sidebar (sans titre,
              etoiles un peu plus grandes pour etre plus lisibles). */}
          {(() => {
            const NOTATION_LABELS = { flexibilite: 'Flexibilité', reactivite: 'Réactivité' };
            const n = contact.notations || {};
            const items = Object.entries(NOTATION_LABELS).filter(([k]) => Number(n[k] || 0) > 0);
            if (items.length === 0) return null;
            return (
              <div style={{ marginTop: 16, marginBottom: 14, padding: '12px 0', borderTop: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>
                {items.map(([k, label]) => {
                  const v = Number(n[k] || 0);
                  return (
                    <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 8 }}>
                      <span style={{ color: '#475569', minWidth: 90 }}>{label}</span>
                      <span style={{ display: 'inline-flex', gap: 2 }}>
                        {[1, 2, 3, 4, 5].map((s) => (
                          <StarIcon key={s} size={18} fill={s <= v ? '#fbbf24' : 'none'} color={s <= v ? '#fbbf24' : '#cbd5e1'} />
                        ))}
                      </span>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          <hr className="sidebar-divider" />

          <div className="contact-quick-info">
            {contact.tel_portable && String(contact.tel_portable).trim() !== '0' && String(contact.tel_portable).trim() !== '' && (
              (() => {
                const raw = String(contact.tel_portable).trim();
                // Build tel: URL with country prefix. "0612345678" → "+33612345678"
                const digits = raw.replace(/[^\d+]/g, '');
                const telHref = digits.startsWith('+')
                  ? digits
                  : digits.startsWith('0')
                    ? '+33' + digits.substring(1)
                    : digits;
                return (
                  <div className="quick-info-item">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                    <a href={`tel:${telHref}`}>{raw}</a>
                    <CopyButton value={raw} label="le téléphone" />
                  </div>
                );
              })()
            )}
            {!isEmptyValue(contact.not_share_phone_number) ? (
              <div className="no-share-warning">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                ne pas communiquer le portable aux clients
              </div>
            ) : null}
            {!isEmptyValue(contact.email) && (
              <div className="quick-info-item">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                <a href={`mailto:${contact.email}`}>{contact.email}</a>
                <CopyButton value={contact.email} label="l'e-mail" />
              </div>
            )}
            {contact.adresse && (
              <>
                <div className="quick-info-item address-block">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  <span>
                    {contact.nom_adresse && <>{contact.nom_adresse}<br/></>}
                    {contact.adresse}<br/>
                    {contact.cp} {contact.ville}
                    {contact.pays_nom && contact.pays_nom !== 'France' && <><br/>{contact.pays_nom}</>}
                    {contact.adresse_complementaire && <><br/>{contact.adresse_complementaire}</>}
                  </span>
                </div>
                {contact.distance && contact.antenne_ville && (
                  <div className="distance-info">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 16L14.5 21.5L13.08 20.09L16.17 17H10.5C6.91 17 4 14.09 4 10.5V4H6V10.5C6 13 8 15 10.5 15H16.17L13.09 11.91L14.5 10.5L20 16Z" />
                    </svg>
                    Environ {contact.distance} du centre de {contact.antenne_ville}
                  </div>
                )}
                {/* Mappy en direct, comme dans le CRM ; le calculateur de frais
                    (distance, péages, grille de l'antenne) à côté. */}
                <span className="mappy-link" style={{ display: 'inline-flex', gap: 12, flexWrap: 'wrap' }}>
                  <a
                    href={`https://fr.mappy.com/itineraire#/vers/${encodeURIComponent(
                      contact.addr_lat && contact.addr_lng
                        ? `${contact.addr_lat},${contact.addr_lng}`
                        : [contact.adresse, contact.cp, contact.ville].filter(Boolean).join(' ')
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'inherit', textDecoration: 'underline' }}
                  >
                    Itinéraire Mappy
                  </a>
                  <button
                    type="button"
                    onClick={() => setShowItineraireModal(true)}
                    style={{ cursor: 'pointer', border: 'none', background: 'transparent', textAlign: 'left', padding: 0, font: 'inherit', color: 'inherit', textDecoration: 'underline' }}
                  >
                    Frais de déplacement
                  </button>
                </span>
              </>
            )}
          </div>

          {hasLocation && (
            <div
              className="sidebar-map sidebar-map-clickable"
              onClick={() => setShowItineraireModal(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowItineraireModal(true); } }}
              title="Agrandir la carte"
            >
              {/* Voile transparent : sans lui, Leaflet capte le clic et la carte
                  se contente de zoomer au lieu de s'ouvrir en grand. */}
              <span className="sidebar-map-overlay" aria-hidden />
              <MapContainer center={position} zoom={13} scrollWheelZoom={false}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <Marker position={position}>
                  <Popup>{contact.prenom} {contact.nom}</Popup>
                </Marker>
              </MapContainer>
            </div>
          )}

          {/* Expédition : ouvre l'app UPS sur ce contact, dans un nouvel onglet.
              L'URL de base est configurable — elle pointe aujourd'hui sur une IP,
              elle changera. Affiché même sans coordonnées GPS : envoyer un colis
              ne dépend pas de la présence d'une carte. */}
          <a
            className="colis-link"
            href={`${UPS_URL}/shipping?antenne=${contact.id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 8v13H3V8" />
              <path d="M1 3h22v5H1z" />
              <path d="M10 12h4" />
            </svg>
            Envoyer un colis
          </a>

          <hr className="sidebar-divider" />

          {/* Team Selfizee duration */}
          {!isEmptyValue(contact.travaille_depuis) ? (
            <p className="team-duration">
              Team Selfizee depuis {calculateTeamDuration(contact.travaille_depuis)} ({new Date(contact.travaille_depuis).toLocaleDateString('fr-FR')})
            </p>
          ) : null}

          {/* Rentrer Selfizee source */}
          {!isEmptyValue(contact.rentrer_selfizee) ? (
            <p className="source-info">
              Arrivé(e) via {getRentrerSelfizeeLabel(contact.rentrer_selfizee)}
              {!isEmptyValue(contact.precisions_rentrer_selfizee) ? <><br/>{contact.precisions_rentrer_selfizee}</> : null}
            </p>
          ) : null}

          {/* SIRET with pappers.fr link */}
          {!isEmptyValue(contact.siret) ? (
            <p className="siret-info">
              Siret : {contact.siret}
              <a
                href={`https://pappers.fr/entreprise/check-${contact.siret.replace(/\D/g, '').substring(0, 9)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="pappers-link"
                title="Vérifier sur Pappers"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </a>
            </p>
          ) : null}
          </div>
        </aside>

        {/* Main Content */}
        <main className="contact-main">
          <div className="tabs-container">
            <div className="tabs">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  className={`tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <span className="tab-label">{tab.label}</span>
                  {tab.count !== undefined && tab.count > 0 && (
                    <span className="tab-count">{tab.count}</span>
                  )}
                  {tab.highlight && (
                    <span title="Tarif personnalisé" style={{ marginLeft: 6, display: 'inline-flex', alignItems: 'center', gap: 2, padding: '1px 6px', borderRadius: 999, background: '#fef3c7', color: '#92400e', fontSize: 10, fontWeight: 700 }}>★</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="tab-content">
            {activeTab === 'informations' && (
              <div className="info-section">
                <div className="info-card">
                  <h3>Informations interne</h3>
                  {(() => {
                    // Legacy : ces colonnes ont parfois "0" ou "1" hérités de l'ancien CRM
                    // (anciennement BOOLEAN). On les ignore à l'affichage.
                    const isLegacyFlag = (v) => {
                      if (v === null || v === undefined) return true;
                      const s = String(v).trim();
                      return s === '' || s === '0' || s === '1';
                    };
                    const tarifMode = contact.tarif_mode === 'specifique' ? 'specifique' : 'base';
                    return (
                      <>
                        {!isEmptyValue(contact.bon_savoir) && !isLegacyFlag(contact.bon_savoir) ? (
                          <div className="info-block">
                            <label className="label-large">Bon à savoir</label>
                            <p className="field-description">Précisez ici : les dispo / indispo, informations pratiques, moyen de communication, organisation, description de l'antenne …</p>
                            <div className="rich-content" dangerouslySetInnerHTML={{ __html: cleanHtml(contact.bon_savoir) }} />
                          </div>
                        ) : null}

                        {/* Compétences : on n'affiche que celles cochées. Si aucune
                            n'est cochée, on n'affiche pas du tout la section. */}
                        {(() => {
                          // Libelles Technicien/Animateur accordes selon la civilite
                          // (M / Mr / male / m → masculin, sinon feminin).
                          const civ = String(contact.civilite || '').trim().toLowerCase();
                          const isMale = ['m', 'mr', 'male'].includes(civ);
                          const COMP_LABELS = {
                            photographie: 'Photographie',
                            animateur: isMale ? 'Animateur' : 'Animatrice',
                            technicien: isMale ? 'Technicien sur place' : 'Technicienne sur place',
                            depannage_info: 'Dépannage informatique',
                            deplacements_specifiques: 'Déplacements spécifiques',
                          };
                          const c = contact.competences || {};
                          const activeKeys = Object.keys(COMP_LABELS).filter((k) => c[k]?.actif);
                          if (activeKeys.length === 0) return null;
                          return (
                            <div className="info-block">
                              <label className="label-large">Compétences</label>
                              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14 }}>
                                {activeKeys.map((k) => (
                                  <li key={k} style={{ marginBottom: 4 }}>
                                    <strong>{COMP_LABELS[k]}</strong>
                                    {c[k]?.commentaire ? <span style={{ color: '#64748b' }}> — {c[k].commentaire}</span> : null}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          );
                        })()}

                        {/* Stockage : section affichée si actif OU si commentaire renseigné. */}
                        {(contact.stockage?.actif || (contact.stockage?.commentaire && String(contact.stockage.commentaire).trim() !== '')) && (
                          <div className="info-block">
                            <label className="label-large">Stockage</label>
                            <p style={{ margin: 0, fontSize: 14 }}>
                              {contact.stockage?.actif ? (
                                <>Propose du stockage{contact.stockage?.commentaire ? <span style={{ color: '#64748b' }}> — {contact.stockage.commentaire}</span> : null}</>
                              ) : (
                                <span style={{ color: '#64748b' }}>{contact.stockage.commentaire}</span>
                              )}
                            </p>
                          </div>
                        )}

                        {/* Informations de facturation : soit la mention "Tarif standard"
                            (le détail s'ouvre via le bouton "Tarifs du client" en haut),
                            soit le texte spécifique. */}
                        {/* Informations comptables — code fournisseur et IBAN.
                            Le numéro IBAN lui-même n'est pas affiché : seule sa
                            présence l'est, l'écran servant au suivi et non au paiement. */}
                        <div className="info-block">
                          <label className="label-large">Informations comptables</label>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, fontSize: 13, color: '#475569' }}>
                            <div>
                              <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.03em', color: '#94a3b8' }}>Code comptable fournisseur</div>
                              <div style={{ fontWeight: 600 }}>{contact.code_comptable_fournisseur || '—'}</div>
                            </div>
                            <div>
                              <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.03em', color: '#94a3b8' }}>IBAN enregistré</div>
                              <div style={{ fontWeight: 600 }}>
                                {contact.iban ? <span style={{ color: '#059669' }}>Oui</span> : <span style={{ color: '#94a3b8' }}>Non</span>}
                                {contact.bic ? <span style={{ marginLeft: 8, fontWeight: 400, color: '#64748b' }}>BIC {contact.bic}</span> : null}
                              </div>
                            </div>
                            <div>
                              <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.03em', color: '#94a3b8' }}>IBAN présent dans l’EDI</div>
                              <div style={{ fontWeight: 600 }}>
                                {Number(contact.iban_added_in_edi) === 1
                                  ? <span style={{ color: '#059669' }}>Oui</span>
                                  : <span style={{ color: '#94a3b8' }}>Non</span>}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="info-block">
                          <label className="label-large">Informations de facturation</label>
                          {tarifMode === 'base' ? (
                            <p style={{ margin: 0, fontSize: 13, color: '#475569', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                              <strong>Tarif standard</strong>
                              <button
                                type="button"
                                onClick={() => setActiveTab('tarif')}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4338ca', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
                                title="Voir la grille tarif appliquée"
                              >
                                Voir
                              </button>
                              {tarifIsCustom && (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 999, background: '#fef3c7', color: '#92400e', fontSize: 11, fontWeight: 600 }}>★ personnalisé</span>
                              )}
                            </p>
                          ) : !isEmptyValue(contact.information_facturation) && !isLegacyFlag(contact.information_facturation) ? (
                            <>
                              <div className="rich-content" dangerouslySetInnerHTML={{ __html: cleanHtml(contact.information_facturation) }} />
                              <div style={{ marginTop: 8 }}>
                                <button type="button" onClick={() => setActiveTab('tarif')} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4338ca', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Voir le tarif</button>
                              </div>
                            </>
                          ) : (
                            <p className="field-description" style={{ margin: 0 }}>
                              Tarif spécifique : aucune information renseignée.
                              <button type="button" onClick={() => setActiveTab('tarif')} style={{ marginLeft: 10, padding: '4px 10px', borderRadius: 6, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4338ca', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Voir le tarif</button>
                            </p>
                          )}
                        </div>

                        {/* Véhicules : ne s'affiche QUE si le contact en a au moins un
                            (liste multi-vehicules OU vehicule legacy avec au moins un champ rempli). */}
                        {(() => {
                          const hasMulti = Array.isArray(contact.vehicules) && contact.vehicules.length > 0;
                          const hasLegacy = contact.vehicule && (contact.vehicule.type || contact.vehicule.couleur || contact.vehicule.immatriculation || contact.vehicule.commentaire);
                          if (!hasMulti && !hasLegacy) return null;
                          return (
                            <div className="info-block">
                              <label className="label-large">Véhicule(s)</label>
                              <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '8px 0' }} />
                              {hasMulti ? (
                                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14 }}>
                                  {contact.vehicules.map((v, i) => (
                                    <li key={v.id || i} style={{ marginBottom: 4 }}>
                                      <strong>{[v.marque, v.modele].filter(Boolean).join(' ')}</strong>
                                      {v.couleur ? <span style={{ color: '#64748b' }}> · {v.couleur}</span> : null}
                                      {v.immatriculation ? <span style={{ color: '#64748b' }}> · {v.immatriculation}</span> : null}
                                      {v.commentaire ? <span style={{ color: '#64748b' }}> — {v.commentaire}</span> : null}
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 14 }}>
                                  {contact.vehicule.type && <div><strong style={{ color: '#64748b', fontWeight: 500 }}>Type :</strong> {contact.vehicule.type}</div>}
                                  {contact.vehicule.couleur && <div><strong style={{ color: '#64748b', fontWeight: 500 }}>Couleur :</strong> {contact.vehicule.couleur}</div>}
                                  {contact.vehicule.immatriculation && <div><strong style={{ color: '#64748b', fontWeight: 500 }}>Immatriculation :</strong> {contact.vehicule.immatriculation}</div>}
                                  {contact.vehicule.commentaire && <div style={{ gridColumn: '1 / -1' }}><strong style={{ color: '#64748b', fontWeight: 500 }}>Commentaire :</strong> {contact.vehicule.commentaire}</div>}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </>
                    );
                  })()}

                  {Array.isArray(contact.contacts_lies) && contact.contacts_lies.filter((c) => c && c.trim()).length > 0 && (
                    <div className="info-block">
                      <label className="label-large">Contacts liés</label>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {contact.contacts_lies.filter((c) => c && c.trim()).map((cl, i) => (
                          <li key={i} style={{ fontSize: 14, marginBottom: 4 }}>{cl}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {!isEmptyValue(contact.preference_livraison) && (
                    <div className="info-block">
                      <label className="label-large">Préférence de livraison</label>
                      <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{contact.preference_livraison}</p>
                    </div>
                  )}

                </div>

                {/* Informations publiques */}
                <div className="info-card">
                  <h3>Informations publiques</h3>
                  <div className="info-notice">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="16" x2="12" y2="12" />
                      <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                    <span>Informations accessibles pour nos clients. Merci de rédiger correctement et sans faute d'orthographe.</span>
                  </div>
                  {!isEmptyValue(contact.precisions_lieu) && (
                    <div className="info-block">
                      <label>Précisions sur le lieu (accessibilité, description…)</label>
                      <p className="field-hint">Il s'agit d'une maison individuelle, merci de vous garer dans l'allée directement</p>
                      <div className="rich-content" dangerouslySetInnerHTML={{ __html: cleanHtml(contact.precisions_lieu) }} />
                    </div>
                  )}
                  <div className="info-block">
                    <label>Préférences d'horaires de pickup retrait</label>
                    {isEmptyValue(contact.preferences_horaires_pickup_retrait)
                      ? <p className="text-muted">Pas de préférences d'horaires de pickup retrait</p>
                      : <div className="rich-content" dangerouslySetInnerHTML={{ __html: cleanHtml(contact.preferences_horaires_pickup_retrait) }} />
                    }
                  </div>
                  <div className="info-block">
                    <label>Préférences d'horaires de pickup retour</label>
                    {isEmptyValue(contact.preferences_horaires_pickup_retour)
                      ? <p className="text-muted">Pas de préférences d'horaires de pickup retour</p>
                      : <div className="rich-content" dangerouslySetInnerHTML={{ __html: cleanHtml(contact.preferences_horaires_pickup_retour) }} />
                    }
                  </div>
                </div>

                {/* Zone de livraison */}
                <div className="info-card">
                  <h3>Livraison</h3>
                  <div className="info-block">
                    {isEmptyValue(contact.accept_delivery)
                      ? <p className="text-muted">L'antenne ne souhaite pas livrer ses clients pour un forfait unique de 90 € dans une zone prédéfinie</p>
                      : <p className="delivery-status">L'antenne souhaite livrer ses clients pour un forfait unique de 90 € dans une zone prédéfinie</p>
                    }
                  </div>
                  {!isEmptyValue(contact.accept_delivery) && !isEmptyValue(contact.delivery_area) && (
                    <div className="info-block">
                      <label>Zone de livraison</label>
                      <div className="rich-content" dangerouslySetInnerHTML={{ __html: cleanHtml(contact.delivery_area) }} />
                    </div>
                  )}
                </div>

                {/* Activités / Historique */}
                <div className="activities-card-enhanced">
                  <div className="activities-card-header">
                    <div className="activities-title-section">
                      <h3>Historique des activités</h3>
                      <span className="activities-total-count">{contact.historique?.length || 0} entrées</span>
                    </div>
                    <div className="activities-filters">
                      <button
                        className={`filter-pill ${activitiesFilter === 'all' ? 'active' : ''}`}
                        onClick={() => setActivitiesFilter('all')}
                      >
                        Tout
                      </button>
                      <button
                        className={`filter-pill ${activitiesFilter === 'comments' ? 'active' : ''}`}
                        onClick={() => setActivitiesFilter('comments')}
                      >
                        Commentaires ({contact.historique?.filter(h => !isActionEntry(h)).length || 0})
                      </button>
                      <button
                        className={`filter-pill ${activitiesFilter === 'actions' ? 'active' : ''}`}
                        onClick={() => setActivitiesFilter('actions')}
                      >
                        Actions ({contact.historique?.filter(h => isActionEntry(h)).length || 0})
                      </button>
                    </div>
                  </div>

                  {/* Comment Form */}
                  <div
                    className={`comment-form-section ${commentDragActive ? 'is-dragging' : ''}`}
                    onDragOver={handleCommentDragOver}
                    onDragEnter={handleCommentDragOver}
                    onDragLeave={handleCommentDragLeave}
                    onDrop={handleCommentDrop}
                  >
                    {commentDragActive && (
                      <div className="comment-drop-overlay">
                        <div className="comment-drop-message">
                          📎 Déposez vos fichiers pour les joindre au commentaire
                        </div>
                      </div>
                    )}
                    <form onSubmit={handleSubmitComment}>
                      <div className="comment-editor-toolbar">
                        <button type="button" onClick={() => handleCommentFormat('bold')} title="Gras"><strong>B</strong></button>
                        <button type="button" onClick={() => handleCommentFormat('italic')} title="Italique"><em>I</em></button>
                        <button type="button" onClick={() => handleCommentFormat('underline')} title="Souligné"><u>U</u></button>
                        <span className="toolbar-divider" />
                        <button type="button" className="file-upload-btn" title="Joindre des fichiers" onClick={() => fileInputRef.current?.click()}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                          </svg>
                        </button>
                        <input ref={fileInputRef} type="file" multiple onChange={handleCommentFileChange} style={{ display: 'none' }} />
                      </div>
                      <div
                        ref={commentRef}
                        className="comment-editable"
                        contentEditable
                        data-placeholder="Ajouter un commentaire..."
                        onInput={(e) => setCommentHasContent(!!e.currentTarget.textContent.trim())}
                      />
                      {commentFiles.length > 0 && (
                        <div className="comment-files-preview">
                          {commentFiles.map((file, i) => {
                            const ext = file.name.split('.').pop().toLowerCase();
                            const isImg = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
                            return (
                              <div key={i} className="comment-file-chip">
                                {isImg ? (
                                  <img src={URL.createObjectURL(file)} alt={file.name} className="comment-file-thumb" />
                                ) : (
                                  <span className="comment-file-ext">{ext.toUpperCase()}</span>
                                )}
                                <span className="comment-file-name">{file.name}</span>
                                <button type="button" className="comment-file-remove" onClick={() => removeCommentFile(i)}>&times;</button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {(commentHasContent || commentFiles.length > 0) && (
                        <div className="comment-form-actions">
                          <button type="submit" className="btn btn-primary btn-sm" disabled={submittingComment}>
                            {submittingComment ? 'Envoi...' : 'Publier le commentaire'}
                          </button>
                        </div>
                      )}
                    </form>
                  </div>

                  {filteredActivities.length > 0 ? (
                    <div className="activities-timeline">
                      {displayedActivities.map((h, index) => (
                        <div key={h.id} className={`activity-card ${isActionEntry(h) ? 'is-action' : 'is-comment'}`}>
                          <div className="activity-card-content">
                            <div className="activity-card-header">
                              <div className="activity-author-info">
                                {h.user_photo ? (
                                  <img src={getGatewayUploadsUrl(h.user_photo)} alt="" className="activity-avatar-photo" />
                                ) : (
                                  <div className="activity-avatar-sm">
                                    {(h.display_name || 'U').charAt(0).toUpperCase()}
                                  </div>
                                )}
                                <div>
                                  <span className="activity-author-name">{h.display_name || 'Utilisateur'}</span>
                                  <span className="activity-timestamp">
                                    {new Date(h.created).toLocaleDateString('fr-FR', {
                                      day: 'numeric',
                                      month: 'long',
                                      year: 'numeric'
                                    })} à {new Date(h.created).toLocaleTimeString('fr-FR', {
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </span>
                                </div>
                              </div>
                              <div className="activity-badges">
                                {!h.is_story && currentUserId && h.user_id === currentUserId && (
                                  <div className="activity-owner-actions">
                                    <button
                                      className="activity-action-btn activity-edit-btn"
                                      onClick={() => handleEditComment(h)}
                                      title="Modifier"
                                    >
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                      </svg>
                                    </button>
                                    <button
                                      className="activity-action-btn activity-delete-btn"
                                      onClick={() => setDeletingComment(h)}
                                      title="Supprimer"
                                    >
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="3 6 5 6 21 6" />
                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                      </svg>
                                    </button>
                                  </div>
                                )}
                                {h.id_trello && <span className="activity-source-badge trello">Trello</span>}
                                <span className={`activity-type-badge ${isActionEntry(h) ? 'type-action' : 'type-comment'}`}>
                                  {isActionEntry(h) ? 'Action' : 'Commentaire'}
                                </span>
                              </div>
                            </div>
                            <div className="activity-card-body" dangerouslySetInnerHTML={{ __html: cleanHtml((h.formatted_comment || h.commentaire || h.contenu || '').replace(/\r\n/g, '<br>').replace(/\r/g, '<br>').replace(/\n/g, '<br>').replace(/\t/g, '&emsp;')) }} />
                            {/* Attached files */}
                            {h.files && h.files.length > 0 && (
                              <div className="activity-files">
                                {h.files.map(file => {
                                  const isBroken = brokenFiles.has(file.id);
                                  if (isBroken) {
                                    return (
                                      <div key={file.id} className="activity-file-missing" title="Fichier manquant">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <circle cx="12" cy="12" r="10" />
                                          <line x1="12" y1="8" x2="12" y2="12" />
                                          <line x1="12" y1="16" x2="12.01" y2="16" />
                                        </svg>
                                        <span>{file.nom || file.fichier}</span>
                                      </div>
                                    );
                                  }
                                  const fileUrl = getHistoriqueFileUrl(file.fichier);
                                  const ext = (file.fichier || '').split('.').pop().toLowerCase();
                                  const isImg = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext);
                                  return (
                                    <div
                                      key={file.id}
                                      className="activity-file-item activity-file-clickable"
                                      onClick={() => openPreview({ url: fileUrl, name: file.nom || file.fichier, fichier: file.fichier })}
                                    >
                                      <div className="activity-file-preview">
                                        {isImg ? (
                                          <img src={fileUrl} alt={file.nom || file.fichier} className="activity-file-thumb" />
                                        ) : (
                                          <span className="activity-file-ext">{ext.toUpperCase() || 'FILE'}</span>
                                        )}
                                      </div>
                                      <div className="activity-file-info">
                                        <span className="activity-file-name">{file.nom || file.fichier}</span>
                                        {/* Pas de <a download> : l'attribut `download` est
                                            IGNORÉ en cross-origin (les uploads sont servis par
                                            l'API), le navigateur se contente d'ouvrir le
                                            fichier. On passe par le même téléchargement blob
                                            que l'onglet Documents. */}
                                        <button
                                          type="button"
                                          className="activity-file-download"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            forceDownload(fileUrl, file.nom || file.fichier);
                                          }}
                                        >
                                          Télécharger
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}

                      {filteredActivities.length > 5 && (
                        <div className="activities-toggle">
                          <button
                            className="btn-toggle-activities"
                            onClick={() => setActivitiesExpanded(!activitiesExpanded)}
                          >
                            {activitiesExpanded ? (
                              <>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="18 15 12 9 6 15" />
                                </svg>
                                Réduire
                              </>
                            ) : (
                              <>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="6 9 12 15 18 9" />
                                </svg>
                                Voir {filteredActivities.length - 5} entrées supplémentaires
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="no-activities">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p>Aucune activité enregistrée</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'tarif' && (
              <div className="info-section">
                <div className="info-card">
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <TagIcon size={16} color={tarifIsCustom ? '#b45309' : '#4f46e5'} />
                    Tarif appliqué
                    {tarifIsCustom ? (
                      <span style={{ fontSize: 12, padding: '2px 8px', borderRadius: 999, background: '#fef3c7', color: '#92400e', fontWeight: 600 }}>★ Tarif personnalisé</span>
                    ) : (
                      <span style={{ fontSize: 12, padding: '2px 8px', borderRadius: 999, background: '#eef2ff', color: '#4338ca', fontWeight: 600 }}>Tarif standard</span>
                    )}
                  </h3>
                  {tarifData ? (
                    <TarifReadOnlyGrid data={tarifData} reference={tarifStandard} />
                  ) : (
                    <p className="field-description" style={{ margin: 0 }}>Aucun tarif disponible pour ce contact.</p>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'documents' && (
              <div className="documents-section">
                {contact.documents && contact.documents.length > 0 ? (
                  <div className="documents-grid">
                    {contact.documents.map(doc => {
                      const isImage = isImageFile(doc.fichier);
                      const fileInfo = getFileTypeInfo(doc.fichier);

                      return (
                        <div key={doc.id} className="document-card-new">
                          <div
                            className="document-preview document-preview-clickable"
                            onClick={() => openPreview({ url: getUploadsUrl(doc.fichier), name: doc.nom || doc.fichier, fichier: doc.fichier })}
                          >
                            {isImage ? (
                              <div className="document-image-link">
                                <img
                                  src={getUploadsUrl(doc.fichier)}
                                  alt={doc.nom || doc.fichier}
                                  className="document-image"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                                <div className="document-image-fallback" style={{ display: 'none' }}>
                                  <span>Image</span>
                                </div>
                              </div>
                            ) : (
                              <div
                                className="document-file-icon"
                                style={{ backgroundColor: fileInfo.bg, color: fileInfo.color }}
                              >
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                  <polyline points="14 2 14 8 20 8" />
                                </svg>
                                <span className="file-ext-label">{fileInfo.label}</span>
                              </div>
                            )}
                          </div>
                          <div className="document-info">
                            <span className="document-type-badge">
                              {doc.type_document_nom || 'Document'}
                            </span>
                            <p className="document-name" title={doc.nom || doc.fichier}>
                              {doc.nom || doc.fichier}
                            </p>
                            <p className="document-date-small">
                              {formatDate(doc.created_at || doc.created)}
                            </p>
                            <div className="document-actions-row">
                              <button
                                className="doc-btn doc-btn-preview"
                                onClick={() => openPreview({ url: getUploadsUrl(doc.fichier), name: doc.nom || doc.fichier, fichier: doc.fichier })}
                                title="Aperçu"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <circle cx="11" cy="11" r="8" />
                                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => forceDownload(getUploadsUrl(doc.fichier), doc.nom || doc.fichier)}
                                className="doc-btn doc-btn-download"
                                title="Télécharger"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                  <polyline points="7 10 12 15 17 10" />
                                  <line x1="12" y1="15" x2="12" y2="3" />
                                </svg>
                              </button>
                              <Gate perm="documents.delete">
                                <button
                                  className="doc-btn doc-btn-delete"
                                  onClick={() => handleDeleteDocument(doc.id)}
                                  title="Supprimer"
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="3 6 5 6 21 6" />
                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                  </svg>
                                </button>
                              </Gate>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="no-data">Aucun document</p>
                )}
              </div>
            )}

            {activeTab === 'binomes' && (
              <div className="binomes-section">
                <div className="section-header">
                  <h3>Binôme(s) lié(s) à ce contact</h3>
                </div>
                {contact.binomes && contact.binomes.length > 0 ? (
                  <div className="binomes-grid">
                    {contact.binomes.map(binome => (
                      <div key={binome.id} className="binome-card-new">
                        <div className="binome-type-badge">Binôme</div>
                        <div className="binome-content">
                          <div className="binome-avatar-section">
                            <Link to={`/contacts/${binome.binome_id}`} className="binome-avatar-link">
                              {binome.binome_photo ? (
                                <img
                                  src={getUploadsUrl(binome.binome_photo)}
                                  alt={`${binome.binome_prenom} ${binome.binome_nom}`}
                                  className="binome-avatar-img"
                                />
                              ) : binome.binome_civilite === 'm' ? (
                                <img src="/img/avatar-homme.svg" alt="Avatar homme" className="binome-avatar-img" />
                              ) : (
                                <img src="/img/avatar-femme.svg" alt="Avatar femme" className="binome-avatar-img" />
                              )}
                            </Link>
                          </div>
                          <div className="binome-details">
                            <h5 className="binome-name">
                              <Link to={`/contacts/${binome.binome_id}`}>
                                {binome.binome_prenom} {binome.binome_nom}
                              </Link>
                            </h5>
                            {binome.binome_raison_sociale && (
                              <p className="binome-company">{binome.binome_raison_sociale}</p>
                            )}
                            <p className="binome-lien">
                              <strong>Lien :</strong> {binome.lien_binome_nom || 'Non précisé'}
                            </p>
                            <p className="binome-precisions">
                              <strong>Précisions :</strong> {binome.precisions || '--'}
                            </p>
                            {binome.binome_tel && (
                              <p className="binome-contact">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                                </svg>
                                <a href={`tel:${binome.binome_tel}`}>{binome.binome_tel}</a>
                              </p>
                            )}
                            {binome.binome_email && (
                              <p className="binome-contact">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                  <polyline points="22,6 12,13 2,6" />
                                </svg>
                                <a href={`mailto:${binome.binome_email}`}>{binome.binome_email}</a>
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-data">Aucun binôme associé</p>
                )}
              </div>
            )}

            {activeTab === 'materiel' && (
              <div className="materiel-section">
                <div className="section-header">
                  <h3>Matériel Selfizee mis à disposition pour l'antenne</h3>
                </div>
                {contact.bornes && contact.bornes.length > 0 ? (
                  <div className="materiel-table-wrapper">
                    <table className="materiel-table">
                      <thead>
                        <tr>
                          <th>Gamme</th>
                          <th>Numéro</th>
                          <th>Modèle</th>
                          <th>Présent depuis</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contact.bornes.map(borne => (
                          <tr key={borne.id}>
                            <td>
                              <span className={`gamme-badge gamme-${(borne.gamme_nom || borne.gamme || 'autre').toLowerCase()}`}>
                                {borne.gamme_nom || borne.gamme || 'Autre'}
                              </span>
                            </td>
                            <td>
                              <a href={getBorneDetailUrl(borne) || undefined} target="_blank" rel="noopener noreferrer" className="borne-numero borne-numero-link">{borne.numero_formated || borne.numero}</a>
                            </td>
                            <td>{isEmptyValue(borne.model_nom) ? '--' : borne.model_nom}</td>
                            <td>
                              {borne.created
                                ? `Présent depuis le ${new Date(borne.created).toLocaleDateString('fr-FR')}`
                                : '--'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}

                {contact.accessoires && contact.accessoires.length > 0 && (
                  <div style={{ marginTop: contact.bornes?.length > 0 ? 24 : 0 }}>
                    <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: '#475569' }}>Accessoires</h4>
                    <div className="materiel-table-wrapper">
                      <table className="materiel-table">
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th>Référence</th>
                            <th>Commentaire</th>
                            <th>Présent depuis</th>
                          </tr>
                        </thead>
                        <tbody>
                          {contact.accessoires.map((acc) => (
                            <tr key={acc.id}>
                              <td>
                                <span
                                  className="gamme-badge"
                                  style={{
                                    background: acc.type_equipement_couleur || '#e2e8f0',
                                    color: '#fff',
                                  }}
                                >
                                  {acc.type_equipement_nom || '—'}
                                </span>
                              </td>
                              <td>{isEmptyValue(acc.equipement_valeur) ? '—' : acc.equipement_valeur}</td>
                              <td>{isEmptyValue(acc.commentaire) ? '—' : acc.commentaire}</td>
                              <td>
                                {acc.date_arrive
                                  ? `Depuis le ${new Date(acc.date_arrive).toLocaleDateString('fr-FR')}`
                                  : (acc.created ? `Ajouté le ${new Date(acc.created).toLocaleDateString('fr-FR')}` : '—')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {(!contact.bornes || contact.bornes.length === 0) && (!contact.accessoires || contact.accessoires.length === 0) && (
                  <p className="no-data">Aucun matériel enregistré pour cette antenne</p>
                )}
              </div>
            )}

            {false && activeTab === 'missions' && (
              <ContactMissionsTab
                contact={{ id: parseInt(id, 10), nom: contact.nom, prenom: contact.prenom, email: contact.email, antenne_id: contact.antenne_id }}
                token={auth.user?.access_token}
              />
            )}

          </div>
        </main>
      </div>

      {/* Edit Comment Modal */}
      {editingComment && (
        <div className="modal-overlay" onClick={() => setEditingComment(null)}>
          <div className="modal-content comment-edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Modifier le commentaire</h3>
              <button className="modal-close" onClick={() => setEditingComment(null)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div
                ref={editCommentRef}
                className="comment-editable comment-edit-area"
                contentEditable
                dangerouslySetInnerHTML={{ __html: cleanHtml(editCommentContent) }}
              />

              {/* Existing files */}
              {editExistingFiles.length > 0 && (
                <div className="edit-files-section">
                  <label className="edit-files-label">Pièces jointes existantes</label>
                  <div className="edit-files-list">
                    {editExistingFiles.map(file => {
                      const fileUrl = getHistoriqueFileUrl(file.fichier);
                      const ext = (file.fichier || '').split('.').pop().toLowerCase();
                      const isImg = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext);
                      return (
                        <div key={file.id} className="edit-file-chip">
                          {isImg ? (
                            <img src={fileUrl} alt={file.nom || file.fichier} className="edit-file-thumb" />
                          ) : (
                            <span className="edit-file-ext">{ext.toUpperCase()}</span>
                          )}
                          <span className="edit-file-name">{file.nom || file.fichier}</span>
                          <button
                            type="button"
                            className="edit-file-remove"
                            onClick={() => handleEditRemoveExistingFile(file.id)}
                            title="Supprimer ce fichier"
                          >&times;</button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* New files to add */}
              {editNewFiles.length > 0 && (
                <div className="edit-files-section">
                  <label className="edit-files-label">Nouveaux fichiers</label>
                  <div className="edit-files-list">
                    {editNewFiles.map((file, i) => {
                      const ext = file.name.split('.').pop().toLowerCase();
                      const isImg = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
                      return (
                        <div key={i} className="edit-file-chip edit-file-new">
                          {isImg ? (
                            <img src={URL.createObjectURL(file)} alt={file.name} className="edit-file-thumb" />
                          ) : (
                            <span className="edit-file-ext">{ext.toUpperCase()}</span>
                          )}
                          <span className="edit-file-name">{file.name}</span>
                          <button
                            type="button"
                            className="edit-file-remove"
                            onClick={() => handleEditRemoveNewFile(i)}
                            title="Retirer"
                          >&times;</button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="edit-files-add">
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => editFileInputRef.current?.click()}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                  </svg>
                  Ajouter des fichiers
                </button>
                <input ref={editFileInputRef} type="file" multiple onChange={handleEditAddFiles} style={{ display: 'none' }} />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setEditingComment(null)}>Annuler</button>
              <button type="button" className="btn btn-primary" onClick={handleSaveEditComment} disabled={savingComment}>
                {savingComment ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Comment Confirmation Modal */}
      {deletingComment && (
        <div className="modal-overlay" onClick={() => setDeletingComment(null)}>
          <div className="modal-content confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header confirm-header">
              <div className="confirm-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <h3>Supprimer le commentaire</h3>
            </div>
            <div className="modal-body confirm-body">
              <p>Êtes-vous sûr de vouloir supprimer ce commentaire ?</p>
              <p className="confirm-warning">Cette action est irréversible.</p>
            </div>
            <div className="modal-footer confirm-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setDeletingComment(null)}>Annuler</button>
              <button type="button" className="btn btn-danger" onClick={handleDeleteComment}>Supprimer</button>
            </div>
          </div>
        </div>
      )}

      {/* File Preview Modal */}
      {previewFile && (
        <div className="modal-overlay" onClick={() => setPreviewFile(null)}>
          <div
            className={`modal-content preview-modal ${isImageFile(previewFile.fichier) ? 'preview-modal--image' : 'preview-modal--doc'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>{previewFile.name}</h3>
              <button className="modal-close" onClick={() => setPreviewFile(null)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="preview-body">
              {previewLoading && (
                <div className="preview-loader">
                  <div className="preview-spinner"></div>
                  <p>Chargement de l'aperçu...</p>
                </div>
              )}
              {isImageFile(previewFile.fichier) ? (
                <img src={previewFile.url} alt={previewFile.name} className="preview-image" onLoad={() => setPreviewLoading(false)} style={previewLoading ? { display: 'none' } : {}} />
              ) : isVideoFile(previewFile.fichier) ? (
                <video src={previewFile.url} controls className="preview-video" onLoadedData={() => setPreviewLoading(false)} style={previewLoading ? { display: 'none' } : {}}>
                  Votre navigateur ne supporte pas la lecture vidéo.
                </video>
              ) : ['pdf', 'txt'].includes(getFileExtension(previewFile.fichier)) ? (
                previewFile.url ? (
                  <iframe
                    src={previewFile.url}
                    className="preview-embed"
                    title={`Aperçu ${getFileExtension(previewFile.fichier).toUpperCase()}`}
                    onLoad={() => setPreviewLoading(false)}
                    style={previewLoading ? { visibility: 'hidden', height: 0 } : {}}
                  />
                ) : null
              ) : (
                <div className="preview-unsupported">
                  <div className="preview-file-icon-lg" style={{ backgroundColor: getFileTypeInfo(previewFile.fichier).bg, color: getFileTypeInfo(previewFile.fichier).color }}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                    <span>{getFileTypeInfo(previewFile.fichier).label}</span>
                  </div>
                  <p>Aperçu non disponible pour ce type de fichier</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-primary btn-sm" onClick={() => forceDownload(previewFile.originalUrl || previewFile.url, previewFile.name)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '0.375rem' }}>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Télécharger
              </button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setPreviewFile(null)}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* Modale de confirmation de suppression (soft delete) */}
      {showTarifsModal && (
        <ContactTarifsModal
          contactId={parseInt(id, 10)}
          contactName={`${contact?.prenom || ''} ${contact?.nom || ''}`.trim() || contact?.raison_sociale || ''}
          onClose={() => setShowTarifsModal(false)}
        />
      )}

      {showItineraireModal && (
        <ItineraireModal
          contact={contact}
          onClose={() => setShowItineraireModal(false)}
        />
      )}

      {showDeleteModal && (
        <div className="confirm-modal-backdrop" onClick={() => !deleting && setShowDeleteModal(false)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-modal-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <h3>Archiver ce contact ?</h3>
            <p>
              Le contact <strong>{contact?.prenom} {contact?.nom}</strong> sera archivé.
              Toutes les données, l'historique et les liens (bornes, binômes, documents) sont conservés pour traçabilité.
              Le contact n'apparaîtra plus dans les listes actives mais reste consultable par les administrateurs.
            </p>
            <div className="confirm-modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
              >
                Annuler
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmDelete}
                disabled={deleting}
                data-perm="antennes:contacts.delete"
              >
                {deleting ? 'Archivage...' : 'Archiver le contact'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
export default ContactDetail;
